/**
 * Chat Panel - Internal Communication sidebar for supplier detail.
 * Mirrors legacy right-hand panel in "Communication audit log" tab.
 * Shows messages between customer and supplier, with send capability.
 *
 * CR-8605: the thread list comes from the messages API itself, so the panel
 * offers exactly the threads the reader can open — every one of their own
 * customer sites that trades with this supplier (relationship or orders), not
 * just relationship rows — each with its unread count. It opens on
 * `?thread=<customer id>` (the bell's link), else the first thread with unread
 * messages, else the site the user is working in.
 *
 * Threads follow the site switcher like every other customer view. A bell can
 * reach an admin for a site they hold but are not viewing; the panel then says
 * which site the conversation is for and offers to switch to it.
 */

import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  coreAPI, suppliersAPI,
  type Site, type SupplierProfile, type SupplierMessageThread as Thread,
} from '../../lib/api';
import { useView } from '../../contexts/ViewContext';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import SupplierMessageThread from '../../components/suppliers/SupplierMessageThread';
import { AlertCircle, Loader2, MessageSquare, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../utils/errorUtils';

interface ChatPanelProps {
  supplier: SupplierProfile;
}

/** The thread to open: the linked one, else unread, else the acting site, else the latest. */
function pickThread(threads: Thread[], linked: number | null): number | null {
  if (threads.length === 0) return null;
  const pick = threads.find((t) => t.customer === linked)
    ?? threads.find((t) => t.unread_count > 0)
    ?? threads.find((t) => t.is_current)
    ?? threads[0];
  return pick ? pick.customer : null;
}

function UnreadBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  return <Badge className="ml-2 h-5 px-1.5 text-[10px]">{count} unread</Badge>;
}

interface SwitchSiteNoticeProps {
  site: Site;
  /** A dual-mode company's user working in the supplier portal: the thread is read as the customer. */
  needsCustomerPortal: boolean;
  onSwitch: () => void;
}

function SwitchSiteNotice({ site, needsCustomerPortal, onSwitch }: SwitchSiteNoticeProps) {
  return (
    <div className="mb-3 rounded-md border border-primary/30 bg-primary/5 p-3 text-sm">
      <p>
        This conversation is for <span className="font-medium">{site.name}</span>.{' '}
        {needsCustomerPortal
          ? 'Switch to the customer portal on that site to read and reply.'
          : 'Switch to that site to read and reply.'}
      </p>
      <Button size="sm" variant="outline" className="mt-2" onClick={onSwitch}>
        {needsCustomerPortal ? 'Switch to customer portal' : `Switch to ${site.name}`}
      </Button>
    </div>
  );
}

export function ChatPanel({ supplier }: ChatPanelProps) {
  const [searchParams] = useSearchParams();
  const linkedThread = Number(searchParams.get('thread')) || null;
  const {
    accessibleSites, selectSite, portalMode, supportsDualMode, viewKey,
  } = useView();

  const [threads, setThreads] = useState<Thread[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let current = true;
    setLoading(true);
    setError(null);
    suppliersAPI.getMessageThreads(supplier.company)
      .then((res) => {
        if (!current) return;
        const rows = res.data || [];
        setThreads(rows);
        setSelectedCustomer(pickThread(rows, linkedThread));
      })
      .catch((err: unknown) => {
        if (!current) return;
        console.error('Failed to fetch message threads:', err);
        setThreads([]);
        setSelectedCustomer(null);
        setError(getApiErrorMessage(err, 'Conversations could not be loaded.'));
      })
      .finally(() => {
        if (current) setLoading(false);
      });
    return () => { current = false; };
    // linkedThread is applied by the effect below once threads are known.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplier.company, reloadKey, viewKey]);

  // A bell clicked while this page is already open changes only ?thread=.
  useEffect(() => {
    if (linkedThread && threads.some((t) => t.customer === linkedThread)) {
      setSelectedCustomer(linkedThread);
    }
  }, [linkedThread, threads]);

  // Opening a thread marks the other side's messages read.
  const handleRead = useCallback(() => {
    setThreads((prev) => prev.map((t) => (
      t.customer === selectedCustomer && t.unread_count > 0 ? { ...t, unread_count: 0 } : t
    )));
  }, [selectedCustomer]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center gap-3 py-12 text-center text-sm">
        <p className="flex items-center gap-1.5 text-destructive">
          <AlertCircle className="h-4 w-4" />
          {error}
        </p>
        <Button variant="outline" size="sm" onClick={() => setReloadKey((k) => k + 1)}>
          <RefreshCw className="h-4 w-4 mr-1" />
          Retry
        </Button>
      </div>
    );
  }

  // The bell's thread is a site the user holds but is not viewing.
  const linkedSite = linkedThread && !threads.some((t) => t.customer === linkedThread)
    ? accessibleSites.find((s) => s.id === linkedThread)
    : undefined;
  const needsCustomerPortal = supportsDualMode && portalMode === 'supplier';
  const switchToLinked = async () => {
    if (!linkedSite) return;
    if (!needsCustomerPortal) {
      selectSite(linkedSite.id, linkedSite.name);
      return;
    }
    // A portal switch reloads the app, as the top-bar toggle does — saved
    // first, with the site, so the reload lands back on this thread.
    try {
      await coreAPI.updateViewContext({
        current_view_level: 'SITE', current_group: null, current_company: null,
        current_site: linkedSite.id, portal_mode: 'customer',
      });
      window.location.reload();
    } catch (err: unknown) {
      console.error('Failed to switch to the customer portal:', err);
      toast.error(getApiErrorMessage(err, 'Could not switch to the customer portal.'));
    }
  };
  const switchNotice = linkedSite
    ? <SwitchSiteNotice site={linkedSite} needsCustomerPortal={needsCustomerPortal} onSwitch={switchToLinked} />
    : null;

  if (threads.length === 0) {
    return (
      <div className="py-6">
        {switchNotice}
        <div className="text-center py-6 text-muted-foreground">
          <MessageSquare className="h-10 w-10 mx-auto mb-3 opacity-50" />
          <p className="text-sm">No customer relationships found for this supplier.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {switchNotice}
      {/* Header + Customer Selector */}
      <div className="pb-3 border-b">
        <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
          <MessageSquare className="h-4 w-4" />
          Internal Communication
        </h3>
        {threads.length > 1 && (
          <Select
            value={selectedCustomer ? String(selectedCustomer) : undefined}
            onValueChange={(value) => setSelectedCustomer(Number(value))}
          >
            <SelectTrigger className="h-9 text-sm" aria-label="Customer site">
              <SelectValue placeholder="Select a customer site" />
            </SelectTrigger>
            <SelectContent>
              {threads.map((thread) => (
                <SelectItem key={thread.customer} value={String(thread.customer)}>
                  <span className="flex items-center">
                    {thread.customer_name}
                    <UnreadBadge count={thread.unread_count} />
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {threads.length === 1 && (
          <Badge variant="outline" className="text-xs">
            {threads[0].customer_name}
          </Badge>
        )}
      </div>

      {selectedCustomer && (
        <SupplierMessageThread
          key={selectedCustomer}
          supplierId={supplier.company}
          customerId={selectedCustomer}
          onRead={handleRead}
        />
      )}
    </div>
  );
}
